import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Auth } from 'aws-amplify'
import { useDispatch } from 'react-redux'
import { WarningIcon } from '@phosphor-icons/react'
import { parseToken } from '../../../api/tokenValidation'
import {
    checkUserExists,
    provisionNewUser,
    recordLogin,
    pollForUserCreation,
} from '../../../api/hooks/googleAuth/googleAuthApi'
import { EVENTS, trackEvent } from '../../../events'
import { ROUTES } from '../../../router/routes'
import { ScoopSpinner, ScoopButton, ScoopText, ScoopCenter, ScoopColumn } from '../../ui'
import styles from './GoogleLoginV2.module.scss'

export const STATUS = {
    CHECKING: 'checking',
    LOADING: 'loading',
    VERIFYING: 'verifying',
    ERROR: 'error',
}

const POLLING_CONFIG = {
    MAX_ATTEMPTS: 5,
    DELAY_MS: 8000, // 8 seconds × 5 attempts = 40 seconds coverage post-504
}

const SUPPORT_EMAIL = 'support@scoopanalytics.com'

export const GoogleLoginV2 = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [status, setStatus] = useState(STATUS.CHECKING)

    const dispatchAuth = (userInfo, idToken, refreshToken) => {
        dispatch({
            type: 'SET_AUTHENTICATED',
            isAuthenticated: true,
            user: {
                signInUserSession: {
                    idToken: { jwtToken: idToken },
                    refreshToken: { token: refreshToken },
                },
                username: userInfo.userID,
                userFirstName: userInfo.firstName,
                attributes: {
                    email: userInfo.email,
                },
            },
        })
    }

    const handleProvisionNewUser = async (userInfo, idToken, refreshToken) => {
        try {
            const response = await provisionNewUser({
                email: userInfo.email,
                userID: userInfo.userID,
                firstName: userInfo.firstName,
                lastName: userInfo.lastName,
            })

            // Success - user created
            dispatchAuth(userInfo, idToken, refreshToken)
            recordLogin(userInfo.userID, idToken)
            navigate(ROUTES.ONBOARDING)
        } catch (error) {
            // Axios throws on non-2xx - check if it's a 504 timeout
            if (error?.response?.status === 504) {
                setStatus(STATUS.VERIFYING)

                const createdUser = await pollForUserCreation(userInfo.email, idToken, {
                    maxAttempts: POLLING_CONFIG.MAX_ATTEMPTS,
                    delayMs: POLLING_CONFIG.DELAY_MS,
                })

                if (createdUser) {
                    dispatchAuth(userInfo, idToken, refreshToken)
                    recordLogin(userInfo.userID, idToken)
                    navigate(ROUTES.ONBOARDING)
                    return
                }

                trackEvent(EVENTS.AUTH.GOOGLE_LOGIN_FAILED, {
                    email: userInfo.email,
                })
            } else {
                console.error('Error provisioning new user:', error)
            }
            setStatus(STATUS.ERROR)
        }
    }

    const checkCurrentSession = useCallback(async () => {
        try {
            const session = await Auth.currentSession()
            const idToken = session.getIdToken().getJwtToken()
            const refreshToken = session.getRefreshToken().getToken()
            const userInfo = parseToken(idToken)

            // Check if user exists in DB
            const existingUser = await checkUserExists(userInfo.email, idToken)

            if (existingUser) {
                // User exists - update userInfo with DB data
                userInfo.userID = existingUser.userID
                userInfo.firstName = existingUser.firstName

                // Dispatch auth and navigate
                dispatchAuth(userInfo, idToken, refreshToken)
                recordLogin(userInfo.userID, idToken)
                navigate(ROUTES.INSIGHTS)
                return
            }

            // User doesn't exist - provision new user
            setStatus(STATUS.LOADING)
            await handleProvisionNewUser(userInfo, idToken, refreshToken)
        } catch (error) {
            console.error('Session check error:', error)
            localStorage.removeItem('isGoogleFlow')
            navigate(ROUTES.LOGIN)
        }
    }, [navigate, dispatch])

    useEffect(() => {
        checkCurrentSession()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleBackToSignUp = () => {
        localStorage.removeItem('isGoogleFlow')
        navigate(ROUTES.SIGNUP)
    }

    const renderContent = () => {
        switch (status) {
            case STATUS.CHECKING:
                return (
                    <>
                        <ScoopSpinner />
                        <ScoopText variant="h5" className={styles.title}>
                            Connecting...
                        </ScoopText>
                    </>
                )

            case STATUS.LOADING:
                return (
                    <>
                        <ScoopSpinner />
                        <ScoopText variant="h5" className={styles.title}>
                            Setting up your Scoop account...
                        </ScoopText>
                        <ScoopText variant="body1" className={styles.subtitle}>
                            This may take a few seconds
                        </ScoopText>
                    </>
                )

            case STATUS.VERIFYING:
                return (
                    <>
                        <ScoopSpinner />
                        <ScoopText variant="h5" className={styles.title}>
                            This is taking longer than expected...
                        </ScoopText>
                        <ScoopText variant="body1" className={styles.subtitle}>
                            We&apos;re finishing setting up your account.
                            <br />
                            Just a moment more.
                        </ScoopText>
                    </>
                )

            case STATUS.ERROR:
                return (
                    <>
                        <WarningIcon size={48} className={styles.errorIcon} />
                        <ScoopText variant="h5" className={styles.title}>
                            We couldn&apos;t create your account
                        </ScoopText>
                        <ScoopText variant="body1" className={styles.subtitle}>
                            There was a problem setting up your account.
                            <br />
                            Please try again.
                        </ScoopText>
                        <ScoopButton className={styles.button} onClick={handleBackToSignUp}>
                            Back to Sign Up
                        </ScoopButton>
                        <ScoopText variant="body2" className={styles.supportText}>
                            If the problem persists, contact support:
                            <br />
                            <a
                                href={`mailto:${SUPPORT_EMAIL}`}
                                className={styles.supportLink}
                                onClick={() =>
                                    trackEvent(EVENTS.ACCOUNT.CONTACT_SUPPORT, {
                                        source: 'google_login_error',
                                    })
                                }
                            >
                                {SUPPORT_EMAIL}
                            </a>
                        </ScoopText>
                    </>
                )

            default:
                return null
        }
    }

    return (
        <ScoopCenter className={styles.container}>
            <ScoopColumn className={styles.content}>{renderContent()}</ScoopColumn>
        </ScoopCenter>
    )
}
